import { useState, useCallback } from 'react';
import { UseInlineEditReturn } from '../../types';

/**
 * Hook for managing inline cell editing functionality
 *
 * This hook provides state management for inline editing of table cells,
 * including tracking which cell is being edited, managing edit values,
 * and handling commit/cancel operations.
 *
 * @template T - The type of data rows being edited
 * @param onCellEdit - Optional callback when a cell edit is committed
 *   Receives: rowId, field, newValue, oldValue
 *
 * @returns Object containing:
 *   - isEditing: Function to check if a specific cell is being edited
 *   - startEdit: Function to start editing a cell
 *   - commitEdit: Function to commit the current edit
 *   - cancelEdit: Function to cancel the current edit
 *   - editingValue: Current value being edited
 *
 * @example
 * ```tsx
 * const { isEditing, startEdit, commitEdit, cancelEdit } = useInlineEdit(
 *   (rowId, field, newValue, oldValue) => {
 *     console.log(`Cell ${field} in row ${rowId} changed from ${oldValue} to ${newValue}`);
 *     // Update your data source here
 *   }
 * );
 *
 * // Start editing a cell
 * startEdit('row-1', 'name', 'John Doe');
 *
 * // Check if a cell is being edited
 * const editing = isEditing('row-1', 'name');
 *
 * // Commit or cancel edit
 * commitEdit('Updated Value');
 * cancelEdit();
 * ```
 */
export const useInlineEdit = <T extends Record<string, unknown>>(
  onCellEdit?: (
    rowId: string | number,
    field: keyof T,
    value: unknown,
    oldValue: unknown,
  ) => void | Promise<void>,
): UseInlineEditReturn<T> => {
  const [editingCell, setEditingCell] = useState<{
    rowId: string | number;
    field: keyof T;
  } | null>(null);
  const [editingValue, setEditingValue] = useState<unknown>(undefined);
  const [originalValue, setOriginalValue] = useState<unknown>(undefined);

  const isEditing = useCallback(
    (rowId: string | number, field: keyof T): boolean => {
      return editingCell?.rowId === rowId && editingCell?.field === field;
    },
    [editingCell],
  );

  const startEdit = useCallback(
    (rowId: string | number, field: keyof T, currentValue: unknown) => {
      setEditingCell({ rowId, field });
      setEditingValue(currentValue);
      setOriginalValue(currentValue);
    },
    [],
  );

  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditingValue(undefined);
    setOriginalValue(undefined);
  }, []);

  const commitEdit = useCallback(
    async (valueToCommit?: unknown) => {
      const finalValue =
        valueToCommit !== undefined ? valueToCommit : editingValue;
      if (editingCell && finalValue !== originalValue) {
        try {
          await onCellEdit?.(
            editingCell.rowId,
            editingCell.field,
            finalValue,
            originalValue,
          );
        } catch (error) {
          // If commit fails, revert to original value
          setEditingValue(originalValue);
          throw error;
        }
      }
      setEditingCell(null);
      setEditingValue(undefined);
      setOriginalValue(undefined);
    },
    [editingCell, editingValue, originalValue, onCellEdit],
  );

  const updateEditingValue = useCallback((value: unknown) => {
    setEditingValue(value);
  }, []);

  return {
    editingCell,
    editingValue,
    isEditing,
    startEdit,
    cancelEdit,
    commitEdit,
    updateEditingValue,
  };
};
